import {
  Expense,
  EXPENSE_CATEGORIES,
  Vehicle,
  expenseAffectsStoreCash,
  MovimentacaoCaixa,
  CategoriaEntradaCaixa,
  CategoriaSaidaCaixa,
  CATEGORIA_ENTRADA_LABEL,
  CATEGORIA_SAIDA_LABEL,
  movimentacaoAfetaSaldoCaixa,
  MaintenanceItem,
  PAYMENT_STATUS,
} from '../types';
import { ExcelReportBuilder } from './excelReportBuilder';
import { formatDateBR, sanitizeFileLabel, toNum } from './reportFormatters';
import type { VehiclesReportVehicle } from '../pages/vehicles/VehiclesReportPDF';
import type { TermSaleReportItem } from '../pages/sales/termSalesReportUtils';
import type { DreResult, TaxEntry } from '../pages/dre/DrePage';

const getExpenseVehicleLabel = (e: Expense): string => {
  const v: any = (e as any).vehicle;
  if (v?.brand || v?.model) {
    const plate = v?.identification ? v.identification : (v?.no_plate ? 'Sem placa' : (v?.plate || '—'));
    return `${v?.brand ?? ''} ${v?.model ?? ''}`.trim() + ` (${plate})`;
  }
  const vid = (e as any).vehicle_id;
  return vid ? `Veículo #${vid}` : '—';
};

const getVehicleIdLabel = (v: { identification?: string; no_plate?: boolean; plate?: string }): string => {
  if (v.identification) return v.identification;
  if (v.no_plate) return 'Sem placa';
  return v.plate || '—';
};

const getVehicleStatusLabel = (status?: string) => {
  switch (status) {
    case 'available': return 'Disponível';
    case 'sold': return 'Vendido';
    case 'maintenance': return 'Manutenção';
    default: return status ?? '—';
  }
};

const getVehicleValue = (v: VehiclesReportVehicle): number => {
  if (typeof v.sale_price === 'number' && v.sale_price > 0) return v.sale_price;
  if (typeof v.sale_price_promo === 'number' && v.sale_price_promo > 0) return v.sale_price_promo;
  if (typeof v.purchase_price === 'number' && v.purchase_price > 0) return v.purchase_price;
  return 0;
};

const getMovCategoryLabel = (m: MovimentacaoCaixa): string => {
  if (!m.category) return '—';
  if (m.type === 'entrada') {
    return CATEGORIA_ENTRADA_LABEL[m.category as CategoriaEntradaCaixa] ?? String(m.category);
  }
  return CATEGORIA_SAIDA_LABEL[m.category as CategoriaSaidaCaixa] ?? String(m.category);
};

const getMovCashLabel = (m: MovimentacaoCaixa): string => {
  if (m.type === 'entrada') return 'Entra';
  return movimentacaoAfetaSaldoCaixa(m) ? 'Debitado' : 'Pendente';
};

const maintenanceStatusLabel = (s: MaintenanceItem['status']) => {
  switch (s) {
    case 'pending': return 'Pendente';
    case 'in_progress': return 'Em andamento';
    case 'done': return 'Concluído';
    case 'cancelled': return 'Cancelado';
    default: return String(s);
  }
};

const maintenancePriorityLabel = (p: MaintenanceItem['priority']) => {
  switch (p) {
    case 'low': return 'Baixa';
    case 'medium': return 'Média';
    case 'high': return 'Alta';
    default: return String(p);
  }
};

export async function exportExpensesReportExcel(params: {
  expenses: Expense[];
  generatedAtLabel: string;
  title?: string;
  filename?: string;
}) {
  const { expenses, generatedAtLabel, title, filename } = params;
  const sorted = [...expenses].sort((a, b) => String(b.expense_date || '').localeCompare(String(a.expense_date || '')));
  const totalAll = sorted.reduce((sum, e) => sum + toNum(e.amount), 0);
  const totalAffectCash = sorted.reduce((sum, e) => sum + (expenseAffectsStoreCash(e) ? toNum(e.amount) : 0), 0);

  const builder = new ExcelReportBuilder('Despesas');
  builder.addTitle(title || 'Relatório de despesas', 7);
  builder.addSubtitle([`Gerado em: ${generatedAtLabel}`, `Total de itens: ${sorted.length}`], 7);
  builder.addSummary([
    { label: 'Total (todas)', value: totalAll, currency: true },
    { label: 'Total que desconta do caixa', value: totalAffectCash, currency: true },
    { label: 'Custo total', value: totalAll, currency: true, bold: true },
  ], 7);
  builder.addTable(
    [
      { header: 'Data', key: 'date', width: 12 },
      { header: 'Veículo', key: 'vehicle', width: 28 },
      { header: 'Categoria', key: 'category', width: 16 },
      { header: 'Descrição', key: 'description', width: 36 },
      { header: 'Observações', key: 'notes', width: 24 },
      { header: 'Valor', key: 'amount', width: 14, currency: true, align: 'right' },
      { header: 'Desconta caixa', key: 'cash', width: 14, align: 'center' },
    ],
    sorted.map((e) => ({
      date: formatDateBR(e.expense_date),
      vehicle: getExpenseVehicleLabel(e),
      category: EXPENSE_CATEGORIES[e.category] ?? String(e.category),
      description: e.description || '—',
      notes: e.notes || '—',
      amount: toNum(e.amount),
      cash: expenseAffectsStoreCash(e) ? 'Sim' : 'Não',
    })),
  );

  await builder.download(filename || `Despesas-${sanitizeFileLabel(generatedAtLabel)}.xlsx`);
}

export async function exportVehiclesReportExcel(params: {
  vehicles: VehiclesReportVehicle[];
  generatedAtLabel: string;
  filename?: string;
}) {
  const { vehicles, generatedAtLabel, filename } = params;

  const builder = new ExcelReportBuilder('Veículos');
  builder.addTitle('Lista de veículos', 8);
  builder.addSubtitle([`Gerado em: ${generatedAtLabel}`, `Total: ${vehicles.length}`], 8);
  builder.addTable(
    [
      { header: 'Marca', key: 'brand', width: 14 },
      { header: 'Modelo', key: 'model', width: 18 },
      { header: 'Ano', key: 'year', width: 8, align: 'center' },
      { header: 'Identificação', key: 'idLabel', width: 14 },
      { header: 'Quilometragem', key: 'km', width: 14 },
      { header: 'Consignado', key: 'consignado', width: 12, align: 'center' },
      { header: 'Valor', key: 'value', width: 16, currency: true, align: 'right' },
      { header: 'Status', key: 'status', width: 14 },
    ],
    vehicles.map((v) => ({
      brand: v.brand,
      model: v.model,
      year: v.year,
      idLabel: getVehicleIdLabel(v),
      km: v.km?.trim() ? `${v.km} km` : '—',
      consignado: v.is_consignado ? 'Sim' : 'Não',
      value: getVehicleValue(v),
      status: getVehicleStatusLabel(v.status),
    })),
  );

  await builder.download(filename || `Veiculos-${sanitizeFileLabel(generatedAtLabel)}.xlsx`);
}

export async function exportMovimentacoesReportExcel(params: {
  movimentacoes: MovimentacaoCaixa[];
  generatedAtLabel: string;
  title?: string;
  subtitle?: string;
  filename?: string;
}) {
  const { movimentacoes, generatedAtLabel, title, subtitle, filename } = params;
  const sorted = [...movimentacoes].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  const totalEntradas = sorted.reduce((sum, m) => sum + (m.type === 'entrada' ? toNum(m.amount) : 0), 0);
  const totalSaidasAll = sorted.reduce((sum, m) => sum + (m.type === 'saida' ? toNum(m.amount) : 0), 0);
  const totalSaidasDebitado = sorted.reduce(
    (sum, m) => sum + (m.type === 'saida' && movimentacaoAfetaSaldoCaixa(m) ? toNum(m.amount) : 0),
    0,
  );
  const totalSaidasPendentes = sorted.reduce(
    (sum, m) => sum + (m.type === 'saida' && !movimentacaoAfetaSaldoCaixa(m) ? toNum(m.amount) : 0),
    0,
  );

  const builder = new ExcelReportBuilder('Movimentações');
  builder.addTitle(title || 'Relatório de movimentações', 9);
  builder.addSubtitle(
    [`Gerado em: ${generatedAtLabel}`, subtitle, `Total de itens: ${sorted.length}`].filter(Boolean) as string[],
    9,
  );
  builder.addSummary([
    { label: 'Total de entradas', value: totalEntradas, currency: true },
    { label: 'Total de gastos (saídas)', value: totalSaidasAll, currency: true },
    { label: 'Saídas debitadas (afetam caixa)', value: totalSaidasDebitado, currency: true },
    { label: 'Saídas pendentes (contas a pagar)', value: totalSaidasPendentes, currency: true },
  ], 9);
  builder.addTable(
    [
      { header: 'Data', key: 'date', width: 12 },
      { header: 'Vencimento', key: 'due', width: 12 },
      { header: 'Tipo', key: 'type', width: 10 },
      { header: 'Caixa', key: 'cash', width: 12 },
      { header: 'Categoria', key: 'category', width: 18 },
      { header: 'Descrição', key: 'description', width: 32 },
      { header: 'Lembrete', key: 'note', width: 24 },
      { header: 'Valor', key: 'amount', width: 14, currency: true, align: 'right' },
      { header: 'Sinal', key: 'sign', width: 8, align: 'center' },
    ],
    sorted.map((m) => ({
      date: formatDateBR(m.date),
      due: formatDateBR(m.dueDate),
      type: m.type === 'entrada' ? 'Entrada' : 'Saída',
      cash: getMovCashLabel(m),
      category: getMovCategoryLabel(m),
      description: m.description || '—',
      note: m.payableNote || '—',
      amount: toNum(m.amount),
      sign: m.type === 'entrada' ? '+' : '−',
    })),
  );

  await builder.download(filename || `Movimentacoes-${sanitizeFileLabel(generatedAtLabel)}.xlsx`);
}

export async function exportTermSalesReportExcel(params: {
  items: TermSaleReportItem[];
  generatedAtLabel: string;
  filterDescription?: string;
  filename?: string;
}) {
  const { items, generatedAtLabel, filterDescription, filename } = params;
  const totalSaleValue = items.reduce((sum, item) => sum + toNum(item.sale.sale_price), 0);
  const totalReceived = items.reduce((sum, item) => sum + item.amountReceived, 0);
  const totalPendingReceive = items.reduce((sum, item) => sum + item.amountPending, 0);
  const totalProfit = items.reduce((sum, item) => sum + item.breakdown.totalProfit, 0);
  const totalProfitRecognized = items.reduce((sum, item) => sum + item.breakdown.recognizedProfit, 0);
  const totalProfitPending = items.reduce((sum, item) => sum + item.breakdown.pendingProfit, 0);

  const builder = new ExcelReportBuilder('Vendas a prazo');
  builder.addTitle('Relatório — Vendas a prazo', 10);
  builder.addSubtitle(
    [`Gerado em: ${generatedAtLabel}`, filterDescription, `${items.length} venda(s)`].filter(Boolean) as string[],
    10,
  );
  builder.addSummary([
    { label: 'Valor total das vendas', value: totalSaleValue, currency: true },
    { label: 'Total já recebido', value: totalReceived, currency: true },
    { label: 'Saldo a receber', value: totalPendingReceive, currency: true },
    { label: 'Lucro total das operações', value: totalProfit, currency: true },
    { label: 'Lucro já no caixa', value: totalProfitRecognized, currency: true },
    { label: 'Lucro ainda a reconhecer', value: totalProfitPending, currency: true },
  ], 10);

  const summaryRows = items.map((item) => ({
    saleId: item.sale.id,
    saleDate: formatDateBR(item.sale.sale_date),
    vehicle: item.vehicleLabel,
    buyer: item.buyerLabel,
    phone: item.buyerPhone || '—',
    salePrice: toNum(item.sale.sale_price),
    downPayment: toNum(item.sale.down_payment),
    paymentStatus: PAYMENT_STATUS[item.sale.payment_status] ?? item.sale.payment_status,
    received: item.amountReceived,
    pending: item.amountPending,
    profitTotal: item.breakdown.totalProfit,
    profitRecognized: item.breakdown.recognizedProfit,
    profitPending: item.breakdown.pendingProfit,
    installmentsNote: item.hasPersistedInstallments ? 'Parcelas persistidas' : 'Parcelas estimadas',
  }));

  builder.addSectionTitle('Resumo por venda', 10);
  builder.addTable(
    [
      { header: 'Venda #', key: 'saleId', width: 10, align: 'center' },
      { header: 'Data', key: 'saleDate', width: 12 },
      { header: 'Veículo', key: 'vehicle', width: 28 },
      { header: 'Cliente', key: 'buyer', width: 22 },
      { header: 'Telefone', key: 'phone', width: 14 },
      { header: 'Valor venda', key: 'salePrice', width: 14, currency: true, align: 'right' },
      { header: 'Entrada', key: 'downPayment', width: 12, currency: true, align: 'right' },
      { header: 'Situação', key: 'paymentStatus', width: 14 },
      { header: 'Recebido', key: 'received', width: 14, currency: true, align: 'right' },
      { header: 'A receber', key: 'pending', width: 14, currency: true, align: 'right' },
    ],
    summaryRows,
    { autoFilter: true, freezeHeader: false },
  );

  builder.addBlank();
  builder.addSectionTitle('Detalhamento de pagamentos e lucro', 10);

  const detailRows = items.flatMap((item) =>
    item.breakdown.rows.map((row) => ({
      saleId: item.sale.id,
      saleDate: formatDateBR(item.sale.sale_date),
      vehicle: item.vehicleLabel,
      buyer: item.buyerLabel,
      payment: row.label,
      paymentAmount: row.paymentAmount,
      profitAmount: row.profitAmount,
      status: row.status === 'paid' ? 'No caixa' : 'Pendente',
      paidAt: row.status === 'paid' && row.paidAt ? formatDateBR(row.paidAt) : '—',
      dueDate: row.dueDate ? formatDateBR(row.dueDate) : '—',
    })),
  );

  builder.addTable(
    [
      { header: 'Venda #', key: 'saleId', width: 10, align: 'center' },
      { header: 'Data venda', key: 'saleDate', width: 12 },
      { header: 'Veículo', key: 'vehicle', width: 24 },
      { header: 'Cliente', key: 'buyer', width: 20 },
      { header: 'Pagamento', key: 'payment', width: 16 },
      { header: 'Valor', key: 'paymentAmount', width: 14, currency: true, align: 'right' },
      { header: 'Lucro (caixa)', key: 'profitAmount', width: 14, currency: true, align: 'right' },
      { header: 'Situação', key: 'status', width: 12 },
      { header: 'Pago em', key: 'paidAt', width: 12 },
      { header: 'Vencimento', key: 'dueDate', width: 12 },
    ],
    detailRows,
  );

  await builder.download(filename || `Vendas-a-prazo-${sanitizeFileLabel(generatedAtLabel)}.xlsx`);
}

export async function exportDreReportExcel(params: {
  dreResult: DreResult;
  dateFrom: string;
  dateTo: string;
  taxEntries: TaxEntry[];
  generatedAtLabel: string;
  filename?: string;
}) {
  const { dreResult, dateFrom, dateTo, taxEntries, generatedAtLabel, filename } = params;
  const taxEntriesWithAmount = taxEntries.filter((e) => toNum(e.amount) > 0);
  const hasTaxas = dreResult.totalTaxas > 0;

  const formatPeriodDate = (s: string) => {
    const [y, m, d] = s.split('-');
    if (!y || !m || !d) return s;
    const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    return `${d}/${m}/${y} (${months[parseInt(m, 10) - 1]}/${y})`;
  };

  const builder = new ExcelReportBuilder('DRE');
  builder.addTitle('Demonstração do Resultado do Exercício (DRE)', 2);
  builder.addSubtitle(
    [
      'R Motors',
      `Período: ${formatPeriodDate(dateFrom)} a ${formatPeriodDate(dateTo)}`,
      `Veículos vendidos: ${dreResult.veiculosVendidos ?? 0}`,
      `Gerado em: ${generatedAtLabel}`,
    ],
    2,
  );
  builder.addBlank();

  const rows: { description: string; value: number; section?: string }[] = [
    { description: 'Receitas', value: 0, section: 'header' },
    { description: 'Faturamento bruto (valor dos veículos)', value: dreResult.faturamentoBrutoVendas },
    { description: 'Vendas (período) — receita de referência (empresa)', value: dreResult.receitaVendas },
    { description: 'Lucro real das vendas (período)', value: dreResult.lucroRealVendas },
    { description: 'Entradas de movimentação de caixa', value: dreResult.receitaMovimentacoes },
    { description: 'Total receitas', value: dreResult.totalReceitas, section: 'total' },
    { description: 'Despesas / Gastos', value: 0, section: 'header' },
    { description: 'Despesas de veículos', value: -dreResult.totalDespesas },
    { description: 'Saídas de movimentação de caixa', value: -dreResult.totalSaidasMovimentacoes },
    { description: 'Total gastos', value: -dreResult.totalGastos, section: 'total' },
  ];

  if (hasTaxas) {
    rows.push({ description: 'Taxas e impostos (manual)', value: 0, section: 'header' });
    taxEntriesWithAmount.forEach((e) => {
      rows.push({ description: e.description || '(Sem descrição)', value: -toNum(e.amount) });
    });
    rows.push({ description: 'Total taxas e impostos', value: -dreResult.totalTaxas, section: 'total' });
  }

  rows.push({ description: 'Resultado (Lucro / Prejuízo)', value: dreResult.resultado, section: 'result' });

  builder.addTable(
    [
      { header: 'Descrição', key: 'description', width: 52 },
      { header: 'Valor (R$)', key: 'value', width: 18, currency: true, align: 'right' },
    ],
    rows.map((r) => ({ description: r.description, value: r.value })),
  );

  await builder.download(filename || `DRE-${dateFrom}-a-${dateTo}.xlsx`);
}

export async function exportVehicleExpensesReportExcel(params: {
  vehicle: Vehicle;
  expenses: Expense[];
  generatedAtLabel: string;
  filename?: string;
}) {
  const { vehicle, expenses, generatedAtLabel, filename } = params;
  const sorted = [...expenses].sort((a, b) => String(b.expense_date || '').localeCompare(String(a.expense_date || '')));
  const totalAll = sorted.reduce((sum, e) => sum + toNum(e.amount), 0);
  const totalAffectCash = sorted.reduce((sum, e) => sum + (expenseAffectsStoreCash(e) ? toNum(e.amount) : 0), 0);
  const purchaseCost = vehicle.is_consignado ? 0 : toNum((vehicle as any).purchase_price);
  const totalCost = purchaseCost + totalAll;

  const builder = new ExcelReportBuilder('Despesas do veículo');
  builder.addTitle('Despesas do veículo', 6);
  builder.addSubtitle([`Gerado em: ${generatedAtLabel}`, `Total de itens: ${sorted.length}`], 6);
  builder.addSummary([
    { label: 'Veículo', value: `${vehicle.brand} ${vehicle.model} (${vehicle.year})` },
    { label: 'Identificação', value: getVehicleIdLabel(vehicle as any) },
    { label: 'Cor', value: vehicle.color || '—' },
    { label: 'Data compra', value: vehicle.purchase_date ? formatDateBR(vehicle.purchase_date) : '—' },
    { label: 'Preço compra', value: vehicle.is_consignado ? 0 : purchaseCost, currency: !vehicle.is_consignado },
    { label: 'Consignado', value: vehicle.is_consignado ? 'Sim' : 'Não' },
    { label: 'Total despesas (todas)', value: totalAll, currency: true },
    { label: 'Total que desconta do caixa', value: totalAffectCash, currency: true },
    { label: 'Custo total (compra + despesas)', value: vehicle.is_consignado ? totalAll : totalCost, currency: true, bold: true },
  ], 6);
  builder.addTable(
    [
      { header: 'Data', key: 'date', width: 12 },
      { header: 'Categoria', key: 'category', width: 16 },
      { header: 'Descrição', key: 'description', width: 36 },
      { header: 'Observações', key: 'notes', width: 24 },
      { header: 'Valor', key: 'amount', width: 14, currency: true, align: 'right' },
      { header: 'Desconta caixa', key: 'cash', width: 14, align: 'center' },
    ],
    sorted.map((e) => ({
      date: formatDateBR(e.expense_date),
      category: EXPENSE_CATEGORIES[e.category] ?? String(e.category),
      description: e.description || '—',
      notes: e.notes || '—',
      amount: toNum(e.amount),
      cash: expenseAffectsStoreCash(e) ? 'Desconta' : 'Não',
    })),
  );

  await builder.download(
    filename || `Despesas-veiculo-${vehicle.id}-${sanitizeFileLabel(generatedAtLabel)}.xlsx`,
  );
}

export async function exportVehicleMaintenanceFutureReportExcel(params: {
  vehicle: Vehicle;
  items: MaintenanceItem[];
  generatedAtLabel: string;
  filename?: string;
}) {
  const { vehicle, items, generatedAtLabel, filename } = params;
  const sorted = [...items].sort((a, b) => String(a.status).localeCompare(String(b.status)));
  const totalEstimated = sorted.reduce(
    (sum, it) => sum + (it.estimated_amount != null ? toNum(it.estimated_amount) : 0),
    0,
  );

  const builder = new ExcelReportBuilder('Manutenções futuras');
  builder.addTitle('Manutenções futuras', 6);
  builder.addSubtitle([`Gerado em: ${generatedAtLabel}`, `Total de itens: ${sorted.length}`], 6);
  builder.addSummary([
    { label: 'Veículo', value: `${vehicle.brand} ${vehicle.model} (${vehicle.year})` },
    { label: 'Identificação', value: getVehicleIdLabel(vehicle as any) },
    { label: 'Cor', value: vehicle.color || '—' },
    { label: 'Total estimado', value: totalEstimated, currency: true, bold: true },
  ], 6);
  builder.addTable(
    [
      { header: 'Status', key: 'status', width: 14 },
      { header: 'Prioridade', key: 'priority', width: 12 },
      { header: 'Descrição', key: 'description', width: 40 },
      { header: 'Observações', key: 'notes', width: 28 },
      { header: 'Estimativa', key: 'estimate', width: 14, currency: true, align: 'right' },
    ],
    sorted.map((it) => ({
      status: maintenanceStatusLabel(it.status),
      priority: maintenancePriorityLabel(it.priority),
      description: it.description || '—',
      notes: it.internal_notes || '—',
      estimate: it.estimated_amount != null ? toNum(it.estimated_amount) : 0,
    })),
  );

  await builder.download(
    filename || `Manutencoes-futuras-${vehicle.id}-${sanitizeFileLabel(generatedAtLabel)}.xlsx`,
  );
}
